import { Document, pdf } from '@react-pdf/renderer';
import type {
  CustomerAnalyticsMoodysAskingRentVacancyDto,
  CustomerAnalyticsMoodysReportDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { AdproductsMoodysReportType } from '@zg-rentals/ts-customer-analytics-client';
import { downloadBlob } from '@zg-rentals/rental-manager-env';
import { CoverPage } from './CoverPage';
import { DemographicsPage, renderDemographicsPage } from './DemographicsPage';
import { renderRentTrendsChart } from './RentTrendsChart';
import { renderVacancyTrendsChart } from './VacancyTrendsChart';
import { renderInventoryChart } from './InventoryChart';
import { MarketMapPage } from './MarketMapPage';
import { renderStaticMarketMap } from './MarketMap';
import { OverviewPage } from './OverviewPage';
import { VacancyTrendsPage } from './VacancyTrendsPage';
import { InventoryPage } from './InventoryPage';
import { ZillowResearchPage } from './ZillowResearchPage';
import { SubmarketExecutiveBrefingPage } from './SubmarketExecutiveBriefingPage';
import { ConcessionsConstructionAbsorptionPage } from './ConcessionsConstructionAbsorptionPage';
import {
  renderScenarioAskingRentLineChart,
  renderScenarioInventoryLineChart,
  renderScenarioVacancyLineChart,
} from './ScenarioLineChart';
import { ScenarioTrendsPage } from './ScenarioLinePage';
import { renderPerformanceChart } from './PerformanceChart';
import { PerformancePage } from './PerformancePage';
import { PerformanceByVintageUnitMixPage } from './PerformanceByVintageUnitMixPage';
import { TopNewConstructionPage } from './TopNewConstructionPage';

interface MarketTrendsPdfProps {
  report: CustomerAnalyticsMoodysReportDto;
  reportsInRange: Array<CustomerAnalyticsMoodysAskingRentVacancyDto>;
  rentTrendsBarChartSrc: string | undefined;
  vacancyTrendsLineChartSrc: string | undefined;
  yearlyInventoryChartSrc: string | undefined;
  marketMapImage: string | undefined;
}
interface PremiumMarketTrendsPdfProps extends MarketTrendsPdfProps {
  scenarioRentChartSrc: string | undefined;
  scenarioVacancyChartSrc: string | undefined;
  scenarioInventoryChartSrc: string | undefined;
  quarterlyInventoryChartSrc: string | undefined;
  performanceChartSrc: string | undefined;
  demographicsChartSrc: string | undefined;
  showKPISection: boolean;
}

export const PremiumMarketTrendsPdf = ({
  report,
  rentTrendsBarChartSrc,
  reportsInRange,
  vacancyTrendsLineChartSrc,
  scenarioInventoryChartSrc,
  scenarioRentChartSrc,
  scenarioVacancyChartSrc,
  yearlyInventoryChartSrc,
  quarterlyInventoryChartSrc,
  performanceChartSrc,
  demographicsChartSrc,
  marketMapImage,
  showKPISection = true,
}: PremiumMarketTrendsPdfProps) => (
  <Document>
    <CoverPage
      quarter={report.dataAsOfQuarter}
      year={report.dataAsOfYear}
      reportType={report.reportType}
      submarketName={report.submarketName}
      marketName={report.marketName}
      isPremium={true}
    />
    {marketMapImage && <MarketMapPage report={report} marketMapImage={marketMapImage} isPremium={true} />}
    {report.zillowResearchBySection && <ZillowResearchPage report={report} />}
    {report.executiveSummaryBySection && <SubmarketExecutiveBrefingPage report={report} />}
    {
      <OverviewPage
        report={report}
        rentTrendsBarChartSrc={rentTrendsBarChartSrc}
        reportsInRange={reportsInRange}
        showKeyPerformanceIndicators={showKPISection}
      />
    }
    {vacancyTrendsLineChartSrc && (
      <VacancyTrendsPage
        report={report}
        vacancyTrendsLineChartSrc={vacancyTrendsLineChartSrc}
        reportsInRange={reportsInRange}
      />
    )}
    {performanceChartSrc && <PerformancePage report={report} performanceChart={performanceChartSrc} />}
    {yearlyInventoryChartSrc && (
      <InventoryPage report={report} yearlyInventoryChart={yearlyInventoryChartSrc} showQuarterly={false} />
    )}
    {quarterlyInventoryChartSrc && (
      <InventoryPage report={report} yearlyInventoryChart={quarterlyInventoryChartSrc} showQuarterly={true} />
    )}
    {scenarioRentChartSrc && (
      <ScenarioTrendsPage report={report} scenarioChartSrc={scenarioRentChartSrc} scenario="RENT" />
    )}
    {scenarioVacancyChartSrc && (
      <ScenarioTrendsPage report={report} scenarioChartSrc={scenarioVacancyChartSrc} scenario="VACANCY" />
    )}
    {scenarioInventoryChartSrc && (
      <ScenarioTrendsPage report={report} scenarioChartSrc={scenarioInventoryChartSrc} scenario="INVENTORY" />
    )}
    {(report.performanceByVintageReport || report.unitMixByConfigType) && (
      <PerformanceByVintageUnitMixPage
        report={report}
        showPerformanceByVintage={!!report.performanceByVintageReport}
        showUnitMix={!!report.unitMixByConfigType}
      />
    )}
    {(report.concessionsReport || report.constructionAbsorptionReport) && (
      <ConcessionsConstructionAbsorptionPage
        report={report}
        showConcessions={report.concessionsReport && report.concessionsReport.length > 0}
        showConstructionAbsorption={!!report.constructionAbsorptionReport}
      />
    )}
    {report.topNewConstructionReport && <TopNewConstructionPage report={report} />}
    {demographicsChartSrc && <DemographicsPage report={report} demographicsChart={demographicsChartSrc} />}
  </Document>
);

export const MarketTrendsPdf = ({
  report,
  rentTrendsBarChartSrc,
  reportsInRange,
  vacancyTrendsLineChartSrc,
  yearlyInventoryChartSrc,
  marketMapImage,
}: MarketTrendsPdfProps) => (
  <Document>
    <CoverPage
      quarter={report.dataAsOfQuarter}
      year={report.dataAsOfYear}
      reportType={report.reportType}
      submarketName={report.submarketName}
      marketName={report.marketName}
    />
    {marketMapImage && <MarketMapPage report={report} marketMapImage={marketMapImage} />}
    {
      <OverviewPage
        report={report}
        rentTrendsBarChartSrc={rentTrendsBarChartSrc}
        reportsInRange={reportsInRange}
        showKeyPerformanceIndicators={true}
      />
    }
    {vacancyTrendsLineChartSrc && (
      <VacancyTrendsPage
        report={report}
        vacancyTrendsLineChartSrc={vacancyTrendsLineChartSrc}
        reportsInRange={reportsInRange}
      />
    )}
    {yearlyInventoryChartSrc && (
      <InventoryPage report={report} yearlyInventoryChart={yearlyInventoryChartSrc} showQuarterly={false} />
    )}
  </Document>
);

export type HideCustomPdfSections = {
  vacancyTrends: boolean;
  rentTrends: boolean;
  marketInventory: boolean;
  submarketInventory: boolean;
  keyPerformanceIndicators: boolean;
};

const downloadPdf = async (PdfElement: () => JSX.Element, reportName: string) => {
  const fileName = `${reportName}-Zillow-Market-Report.pdf`;
  const renderedPdf = pdf(<PdfElement />);
  const blob = await renderedPdf.toBlob();
  downloadBlob(blob, fileName);
};

const getMarketTrendsProps = async (report: CustomerAnalyticsMoodysReportDto, hideSections?: HideCustomPdfSections) => {
  const isPremium = hideSections !== undefined;
  const currentYear = new Date().getFullYear();

  const hasSubmarketData = report.reportType === AdproductsMoodysReportType.SUBMARKET;
  const reportsInRange: Array<CustomerAnalyticsMoodysAskingRentVacancyDto> = (
    hasSubmarketData ? report.askingRentVacancyTrendsReport.submarket : report.askingRentVacancyTrendsReport.market
  ).filter((item) => item.year >= currentYear - 10);

  const rentTrendsBarChartSrc = await renderRentTrendsChart(report);
  const vacancyTrendsLineChartSrc = await renderVacancyTrendsChart(report);
  const yearlyInventoryChart = await renderInventoryChart(report, false);
  const marketMapImage = await renderStaticMarketMap(report);

  const nonPremiumProps = {
    report: report,
    rentTrendsBarChartSrc: rentTrendsBarChartSrc,
    vacancyTrendsLineChartSrc: vacancyTrendsLineChartSrc,
    reportsInRange: reportsInRange,
    yearlyInventoryChartSrc: yearlyInventoryChart,
    marketMapImage: marketMapImage,
  } as MarketTrendsPdfProps;

  if (!isPremium) return nonPremiumProps;

  const performanceChart = await renderPerformanceChart(report);
  const quarterlyInventoryChart = await renderInventoryChart(report, true);
  const scenarioRentChartSrc = await renderScenarioAskingRentLineChart(report);
  const scenarioVacancyChartSrc = await renderScenarioVacancyLineChart(report);
  const scenarioInventoryChartSrc = await renderScenarioInventoryLineChart(report);
  const demographicChartSrc = await renderDemographicsPage(report);

  return {
    ...nonPremiumProps,
    performanceChartSrc: performanceChart,
    quarterlyInventoryChartSrc: quarterlyInventoryChart,
    scenarioRentChartSrc: scenarioRentChartSrc,
    scenarioVacancyChartSrc: scenarioVacancyChartSrc,
    scenarioInventoryChartSrc: scenarioInventoryChartSrc,
    demographicsChartSrc: demographicChartSrc,
    showKPISection: hideSections?.keyPerformanceIndicators !== true,
  } as PremiumMarketTrendsPdfProps;
};

export const renderPremiumMarketTrendsPdf = async (
  report: CustomerAnalyticsMoodysReportDto,
  hideSections: HideCustomPdfSections,
  onError?: (val: boolean) => void,
) => {
  const pdfProps = (await getMarketTrendsProps(report, hideSections)) as PremiumMarketTrendsPdfProps;
  const reportName = report.submarketName ? report.submarketName : report.marketName;

  if (!pdfProps || !reportName) return onError && onError(true);

  downloadPdf(() => <PremiumMarketTrendsPdf {...pdfProps} />, reportName);
};

export const renderMarketTrendsPdf = async (
  report: CustomerAnalyticsMoodysReportDto,
  onError?: (val: boolean) => void,
) => {
  const pdfProps = await getMarketTrendsProps(report);
  const reportName = report.submarketName ? report.submarketName : report.marketName;

  if (!pdfProps || !reportName) return onError && onError(true);

  downloadPdf(() => <MarketTrendsPdf {...pdfProps} />, reportName);
};
